import { useRoleContext } from "./useRoleContext";
import { useSwrFetch } from "./useSwrFetch";

interface UseSentFollowRequestsProps {
  // Lets a caller mounted with its UI hidden (e.g. a closed modal) hold the
  // fetch until the data is actually about to be shown.
  enabled?: boolean;
}

interface UseSentFollowRequestsResult {
  sentFollowRequests: FollowShipperRequestAndResponse[] | undefined;
  loading: boolean;
  error: string | null;
}

/**
 * The calling shipper's partner invites that are still awaiting a response.
 *
 * Every caller shares one SWR key, so the several add-partner affordances on a
 * shipment page resolve to a single request and stay consistent with each other.
 */
export function useSentFollowRequests(
  props: UseSentFollowRequestsProps = {},
): UseSentFollowRequestsResult {
  const { enabled = true } = props;
  const { role } = useRoleContext();

  const url =
    enabled && role?.type === "SHIPPER"
      ? `/shippers/${role.id}/follow_requests/sent`
      : null;

  const { data, loading, error } = useSwrFetch<
    FollowShipperRequestAndResponse[]
  >({
    url,
    errorMessage: "Failed to fetch follow requests",
  });

  return {
    // A failed fetch resolves to no known invites rather than staying undefined,
    // which callers read as "still loading" and would leave waiting for good. The
    // cost is offering an add for a carrier that already has an invite pending;
    // the alternative is an action that silently disappears.
    sentFollowRequests: data ?? (error != null ? [] : undefined),
    loading,
    error,
  };
}
